import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useIsAdmin } from '../auth/AuthContext'
import { Progress, StatusBadge } from '../components/BatchStatus'
import { theme } from '../theme'
import { fmtDateTime } from '../utils/datetime'

/**
 * Internal Tactical E2E — relay a test fax straight to ONE eFax pod's internal NLB.
 *
 * WHAT THIS PAGE IS FOR. A pod pulled from customer routing has no public name and cannot
 * be reached from here at all, so the relay runs inside the provider's own AWS account as
 * a separate process (`e2e_agent`): it polls this scheduler for work, opens a socket on
 * TCP/25 with no MX lookup and no SMTP authentication, and relays bytes WE composed. The
 * agent holds no database, no token logic, no message grammar and no verdict.
 *
 * TWO THINGS THIS PAGE MUST NOT IMPLY, and both are rendered from the server's own words
 * rather than composed here:
 *
 *   1. THAT WE KNOW THE POD IS DRAINED. Nothing we can query says whether a pod is out of
 *      customer routing. So there is no "pod available" badge, and the healthy figure is
 *      labelled capacity OBSERVED — `drain_state` is `unknown` and the note explaining
 *      that comes from `backend/services/internal_e2e.py`.
 *   2. THAT THE ROUND TRIP IS ISOLATED. It is not: the fax is handed to the pod, but the
 *      return copy leaves it, crosses the PSTN, and on a non-eFax inbound leg a second
 *      vendor as well. `isolation_claim` declares that once, per job, and this page
 *      renders the sentence it is given. A page that assembled its own would eventually
 *      describe a different limit from the one that applies.
 *
 * ENVIRONMENT IS THE FIELD THAT MATTERS. `dev-ccs1` and `qa-ccs1` live in ONE account and
 * ONE region, so the operator — not the agent, and not a tag filter — is what tells them
 * apart. A target that declared no `environment` renders the words the server sent for the
 * gap (`environment_not_recorded`), never a plausible substitute, and the choice is
 * restated on the confirmation because that is the last moment it can be caught.
 *
 * WHERE THE DATA COMES FROM, and one deviation from the brief worth naming: the eligible
 * jobs are read from `GET /api/settings/agents/fleet` rather than from
 * `GET /api/jobs?efax_smtp_only=true`. Both apply the same predicate — the launcher's own
 * `is_efax_smtp_outbound` — but only the fleet payload also carries each job's isolation
 * claim, and the alternative was for this page to compose that prose itself. One read, one
 * authority for the words.
 */

const POLL_MS = 5000
const SIZES = [5, 10, 25, 50]

type FleetTarget = {
  id: string
  load_balancer_name: string
  dns_name: string
  pod?: string | null
  region?: string | null
  /** NULL means the target declared no environment tag. Never rendered raw when absent. */
  environment?: string | null
  /** Published as its own fact so the gap is printed, not guessed. */
  environment_recorded: boolean
  /** NULL means not observed. 0 is a real finding and a reason not to launch. */
  healthy_count?: number | null
  last_seen_at?: string | null
}

type FleetAgent = {
  id: string
  name: string
  public_id: string
  enabled: boolean
  last_check_in_at?: string | null
  last_check_in_age_seconds?: number | null
  live: boolean
  reported_aws_account?: string | null
  reported_aws_region?: string | null
  reported_image_version?: string | null
  clock_skew_seconds?: number | null
  targets: FleetTarget[]
}

type Isolation = {
  scope: string
  inbound_provider?: string | null
  inbound_crosses_third_party?: boolean
  note: string
  drain_state: string
  drain_state_note: string
}

type FleetJob = { id: string; name: string; isolation: Isolation }

type Fleet = {
  agents: FleetAgent[]
  eligible_jobs: FleetJob[]
  environment_not_recorded: string
  drain_state: string
  drain_state_note: string
  live_within_seconds: number
}

type Batch = {
  id: string
  job_id: string
  job_name?: string | null
  size: number
  status: string
  // ON THE PAYLOAD ALREADY, and declared here so this page can render the same progress bar
  // the public page does: `BatchResponse` publishes these counts for EVERY batch, public and
  // internal alike. They were simply absent from this type, so the page showed a size and no
  // indication of how far through it was.
  queued: number
  in_flight: number
  terminal: number
  /**
   * ITEMS NOT IN A TERMINAL STATUS, and the age of the oldest of them (R11.7, carried as a
   * reporting signal by decision D-E rather than as the Prometheus series it was drafted as).
   *
   * BOTH, because neither alone is the signal. `open_items` is a depth, and a depth of three
   * that never changes is a stall that looks exactly like healthy churn at depth three — the
   * age is what separates them. The container status cannot: a batch whose agent vanished
   * mid-dispatch sits in a normal-looking status indefinitely.
   *
   * `open_items` is `queued + in_flight` by construction on the server, so this column and
   * the progress bar beside it cannot disagree.
   */
  open_items: number
  /** NULL when nothing is open. Never 0, which would read as an item opened this instant. */
  oldest_open_item_age_seconds?: number | null
  /**
   * DID THIS BATCH MEASURE THE POD IT SAYS IT MEASURED? (B2.27)
   *
   * `'agreement' | 'mismatch' | 'not_recorded'`, typed as `string` for the same reason
   * `PodCrossCheck.outcome` is: the vocabulary is declared in
   * `backend/services/agent_dispatch.py` and a hand-copied union here would be a second
   * declaration that silently stops matching. An unrecognised value renders visibly
   * unrecognised instead.
   *
   * NULL means the question does not arise — a public batch aimed at no pod. That is NOT the
   * same as `not_recorded`, which means a comparison was attempted and nothing was there to
   * compare, and the two must not render alike.
   */
  pod_cross_check_outcome?: string | null
  t_created?: string | null
  target_pod?: string | null
  target_environment?: string | null
  target_environment_recorded?: boolean | null
  target_healthy_at_launch?: number | null
  agent_name?: string | null
}

/**
 * The environment of a target, in words, for both the label and the confirmation.
 *
 * TAKES THE SERVER'S PHRASE as an argument rather than holding a copy of it: the words for
 * a missing tag are declared once, in `internal_e2e.ENVIRONMENT_NOT_RECORDED`, and are
 * published on the payload for exactly this.
 */
function environmentLabel(target: FleetTarget, notRecorded: string): string {
  return target.environment_recorded && target.environment ? target.environment : notRecorded
}

/**
 * An age in seconds on a scale that stays readable from seconds to days.
 *
 * LOCAL BECAUSE THE SHARED HOME IS NOT THIS LANE'S FILE. `AgentSettings.tsx` holds the same
 * s/m/h/d scale as `checkInAge`, and `utils/datetime.ts` is where a third caller should find
 * one `fmtAgeSeconds`. Two copies is the honest state of the tree right now and is reported
 * rather than hidden; `fmtDuration` was the alternative and it renders a four-hour stall as
 * "252m 0s", which is the number this column exists to make someone notice.
 *
 * Rounds rather than truncates, and clamps at zero: a negative age can only come from clock
 * disagreement and "-3s open" reads as a bug in the page rather than in the clocks.
 */
function openAge(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 90) return `${s}s`
  const minutes = Math.round(s / 60)
  if (minutes < 90) return `${minutes}m`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours}h`
  return `${Math.round(hours / 24)}d`
}

/**
 * DID THIS BATCH MEASURE THE POD IT SAYS IT MEASURED? (B2.27) — an INDICATOR, not the detail.
 *
 * The cross-check renders in full on a batch's own report page (`PodCrossCheck.tsx`, which
 * needs the item rows only `GET /api/batches/{id}` loads). A batch launched yesterday and
 * skimmed in this list showed nothing at all, so a mis-attributed batch was found only by
 * somebody already looking at it. This is enough to make them click through, and the detail
 * stays where it is — one authority for the full answer.
 *
 * THREE STATES, NEVER TWO, and the third is the whole point. A mismatch, a match and
 * NOT-YET-KNOWN are different, and rendering an unchecked batch as a confirmed one is the
 * flattering error this product cannot afford: the observed pod is written minutes after the
 * relay, when eFax's send notification is read off IMAP, so "nothing compared yet" is the
 * NORMAL state of a batch that has only just gone out.
 *
 * A DIAGNOSTIC AND NEVER A VERDICT. A fax that went out and came back passed, whichever pod
 * carried it — so this is deliberately not coloured like a pass or a fail, and it must not
 * read as one. What a disagreement devalues is the ATTRIBUTION of the measurement.
 *
 * AN UNRECOGNISED OUTCOME IS RENDERED VISIBLY UNRECOGNISED, following `StatusBadge`: the
 * vocabulary lives in `backend/services/agent_dispatch.py` and this page holds no copy of it,
 * so a value added there appears as something to look at rather than as a blank cell.
 */
const POD_CHECK_LABELS: Record<string, { label: string; colour: string; title: string }> = {
  mismatch: {
    label: '⚠ pod mismatch',
    colour: '#ef5350',
    title:
      'At least one item was handled by a pod other than the one this batch targeted, so ' +
      'the batch does not measure only what it says it measures. Open the report for which ' +
      'items and which pods. This is an attribution finding, not a pass or a fail.',
  },
  agreement: {
    label: 'pod confirmed',
    colour: '#4caf50',
    title:
      "Every item eFax reported a pod for matched this batch's target, on both the pod and " +
      'the environment. The region is deliberately not compared — see the report.',
  },
  not_recorded: {
    label: 'not recorded',
    colour: '#9aa0a6',
    title:
      'NOTHING HAS BEEN COMPARED YET — not a confirmation. The observed pod is written when ' +
      "eFax's send notification is read off IMAP, minutes after the relay, so this is the " +
      'normal state of a batch that has only just gone out.',
  },
}

function PodCheck({ outcome }: { outcome?: string | null }) {
  // NULL means the question does not arise: a public batch aimed at no pod, so there is no
  // intent for an observation to agree or disagree with. Deliberately NOT the same cell as
  // `not_recorded`, which is a statement about a comparison that was attempted.
  if (outcome == null) return <span style={{ color: theme.textMuted }}>—</span>
  const known = Object.prototype.hasOwnProperty.call(POD_CHECK_LABELS, outcome)
  const treatment = known
    ? POD_CHECK_LABELS[outcome]
    : {
        label: `⚠ ${outcome}`,
        colour: '#e040fb',
        title:
          `This page does not recognise the pod cross-check outcome "${outcome}". It is ` +
          'shown as the server sent it. The vocabulary is declared in ' +
          'backend/services/agent_dispatch.py.',
      }
  return (
    <span
      title={treatment.title}
      style={{
        background: treatment.colour + '22',
        color: treatment.colour,
        border: `1px ${known ? 'solid' : 'dashed'} ${treatment.colour}${known ? '55' : 'cc'}`,
        borderRadius: 3,
        padding: '0.1rem 0.45rem',
        fontSize: '0.72rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {treatment.label}
    </span>
  )
}

/**
 * IS THIS BATCH STILL MOVING? (R11.7 as a reporting signal — decision D-E.)
 *
 * Nothing open is rendered as the WORD "none", not a 0 and not a blank: a blank cell is
 * indistinguishable from "this page has nothing to say", which on a stuck-batch column is
 * the one reading it must never produce by accident.
 *
 * An open count WITHOUT an age is rendered as the count plus "age not recorded" rather than
 * silently dropping the age. It should not be reachable — the entry clock is NOT NULL on
 * `batch_run` — so if it ever appears it is a finding, and a column that quietly printed
 * only the count would hide it.
 */
function OpenItems({ b }: { b: Batch }) {
  if (!b.open_items) {
    return <span style={{ color: theme.textMuted }}>none</span>
  }
  const age = b.oldest_open_item_age_seconds
  return (
    <span
      title={
        'Items not in a terminal status, and how long the oldest of them has been open. ' +
        'A count that does not fall is a stall; the age is what tells it from healthy churn.'
      }
      style={{ whiteSpace: 'nowrap' }}
    >
      <strong>{b.open_items}</strong> open
      <span style={{ color: theme.textMuted }}>
        {age == null ? ' · age not recorded' : ` · oldest ${openAge(age)}`}
      </span>
    </span>
  )
}

function targetLabel(target: FleetTarget, notRecorded: string): string {
  const pod = target.pod || target.load_balancer_name
  return `${environmentLabel(target, notRecorded)} · ${pod} · ${target.load_balancer_name}`
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: theme.bgAlt,
        color: theme.text,
        border: `1px solid ${theme.border}`,
        borderRadius: 6,
        padding: '0.9rem',
        fontSize: '0.82rem',
        maxWidth: '46rem',
      }}
    >
      {children}
    </div>
  )
}

export default function InternalE2E() {
  const isAdmin = useIsAdmin()
  const navigate = useNavigate()
  const [fleet, setFleet] = useState<Fleet | null>(null)
  const [batches, setBatches] = useState<Batch[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [launching, setLaunching] = useState(false)
  const [showLaunch, setShowLaunch] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const [agentId, setAgentId] = useState('')
  const [targetId, setTargetId] = useState('')
  const [jobId, setJobId] = useState('')
  const [size, setSize] = useState(5)

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/batches?limit=50&send_path=internal')
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setBatches(d.batches || [])
      setErr(null)
    } catch (e) {
      // Surfaced, not swallowed: a silent fetch failure leaves a page that looks merely
      // idle, which is indistinguishable from "no internal batches yet".
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [])

  const loadFleet = useCallback(async () => {
    try {
      const r = await fetch('/api/settings/agents/fleet')
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      setFleet(await r.json())
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    load()
    loadFleet()
    const t = setInterval(load, POLL_MS)
    return () => clearInterval(t)
  }, [load, loadFleet])

  // A LIVE, ENABLED agent that has reported at least one target. All three, because each
  // absence produces a different useless picker: a disabled agent is refused by the
  // launcher, a dead one will never claim the work, and one with no inventory offers
  // nothing to choose.
  const launchable = (fleet?.agents ?? []).filter(a => a.enabled && a.live && a.targets.length > 0)
  const agent = launchable.find(a => a.id === agentId) ?? null
  const target = agent?.targets.find(t => t.id === targetId) ?? null
  const job = (fleet?.eligible_jobs ?? []).find(j => j.id === jobId) ?? null
  const notRecorded = fleet?.environment_not_recorded ?? 'not recorded'

  async function launch() {
    setLaunching(true)
    setErr(null)
    // Held outside the try so the navigation happens AFTER the finally block has cleared
    // `launching`, and stays null on any failure — which is what keeps a failed launch on
    // this page with its inline error instead of on /tactical/undefined.
    let newBatchId: string | null = null
    try {
      const r = await fetch('/api/batches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: jobId,
          size,
          send_path: 'internal',
          agent_id: agentId,
          target_id: targetId,
        }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        throw new Error(body.detail || `HTTP ${r.status}`)
      }
      const created = await r.json().catch(() => ({}))
      newBatchId = typeof created.id === 'string' && created.id ? created.id : null
      setShowLaunch(false)
      setConfirming(false)
      if (!newBatchId) await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setLaunching(false)
    }
    if (newBatchId) navigate(`/tactical/${newBatchId}`)
  }

  const ready = Boolean(agentId && targetId && jobId)

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem',
        }}
      >
        <h2 style={{ margin: 0 }}>Internal E2E</h2>
        {isAdmin && !showLaunch && (
          <button
            onClick={() => setShowLaunch(true)}
            style={{
              padding: '0.5rem 1rem',
              background: theme.primary,
              color: theme.onPrimary,
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Launch internal batch
          </button>
        )}
      </div>

      <p style={{ color: theme.textMuted, fontSize: '0.82rem', marginTop: 0, maxWidth: '46rem' }}>
        Relays a test fax straight to one eFax pod's internal load balancer, through an agent
        running inside the provider's account. The fax is composed here and sent byte-for-byte
        as a public one would be, so what differs is the path, not the message.
      </p>

      {err && (
        <div
          style={{
            background: '#3a1e1e',
            border: '1px solid #6a2a2a',
            borderRadius: 6,
            padding: '0.7rem',
            marginBottom: '1rem',
          }}
        >
          <span style={{ color: '#ef9a9a', fontSize: '0.82rem' }}>{err}</span>
        </div>
      )}

      {showLaunch && (
        <div
          style={{
            background: theme.bgAlt,
            color: theme.text,
            border: `1px solid ${theme.border}`,
            borderRadius: 6,
            padding: '1rem',
            marginBottom: '1.2rem',
          }}
        >
          <h3 style={{ margin: '0 0 0.8rem', fontSize: '0.95rem' }}>Launch an internal batch</h3>

          {/* BOTH EMPTY STATES SAY SO. An empty select is indistinguishable from a page
              that has not finished loading, and it tells the operator nothing about which
              of the two things is missing. */}
          {launchable.length === 0 ? (
            <Empty>
              <strong>No agent is available to relay.</strong>
              <div style={{ color: theme.textMuted, marginTop: '0.35rem' }}>
                An internal launch needs an agent that is enabled, has polled within{' '}
                {Math.round((fleet?.live_within_seconds ?? 600) / 60)} minutes, and has reported
                at least one load balancer. Check the fleet on{' '}
                <Link to="/settings/agents" style={{ color: theme.primary }}>
                  Settings → Agents
                </Link>
                .
              </div>
            </Empty>
          ) : (fleet?.eligible_jobs.length ?? 0) === 0 ? (
            <Empty>
              <strong>No job is eligible for an internal launch.</strong>
              <div style={{ color: theme.textMuted, marginTop: '0.35rem' }}>
                The outbound leg must be eFax over SMTP: the agent opens a socket and relays
                bytes we composed, so there is no API dialect in that path. Configure a job
                with that outbound leg and it will appear here.
              </div>
            </Empty>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(13rem, 1fr))',
                  gap: '0.9rem',
                }}
              >
                <label style={{ fontSize: '0.78rem', color: theme.textMuted }}>
                  Agent
                  <select
                    value={agentId}
                    onChange={e => {
                      setAgentId(e.target.value)
                      // The target belongs to ONE agent — the launcher checks both
                      // predicates in a single query — so a stale selection from the
                      // previous agent must not survive the change.
                      setTargetId('')
                      setConfirming(false)
                    }}
                    style={{
                      width: '100%',
                      marginTop: '0.25rem',
                      padding: '0.4rem',
                      background: theme.bg,
                      color: theme.text,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                    }}
                  >
                    <option value="">Select an agent…</option>
                    {launchable.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.reported_aws_account || 'account not reported'} ·{' '}
                        {a.reported_aws_region || 'region not reported'})
                      </option>
                    ))}
                  </select>
                </label>

                <label style={{ fontSize: '0.78rem', color: theme.textMuted }}>
                  Target
                  <select
                    value={targetId}
                    onChange={e => {
                      setTargetId(e.target.value)
                      setConfirming(false)
                    }}
                    disabled={!agent}
                    style={{
                      width: '100%',
                      marginTop: '0.25rem',
                      padding: '0.4rem',
                      background: theme.bg,
                      color: theme.text,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                    }}
                  >
                    <option value="">
                      {agent ? 'Select a target…' : 'Select an agent first…'}
                    </option>
                    {/* FROM THE AGENT'S OWN REPORTED INVENTORY, never free text: a pod
                        name we invent is an address the agent cannot reach, and the
                        launch would fail at relay time with nothing to say which part
                        was wrong. The ENVIRONMENT leads the label. */}
                    {(agent?.targets ?? []).map(t => (
                      <option key={t.id} value={t.id}>
                        {targetLabel(t, notRecorded)}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={{ fontSize: '0.78rem', color: theme.textMuted }}>
                  Job
                  <select
                    value={jobId}
                    onChange={e => {
                      setJobId(e.target.value)
                      setConfirming(false)
                    }}
                    style={{
                      width: '100%',
                      marginTop: '0.25rem',
                      padding: '0.4rem',
                      background: theme.bg,
                      color: theme.text,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                    }}
                  >
                    <option value="">Select a job…</option>
                    {(fleet?.eligible_jobs ?? []).map(j => (
                      <option key={j.id} value={j.id}>
                        {j.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={{ fontSize: '0.78rem', color: theme.textMuted }}>
                  Size
                  <select
                    value={size}
                    onChange={e => setSize(Number(e.target.value))}
                    style={{
                      width: '100%',
                      marginTop: '0.25rem',
                      padding: '0.4rem',
                      background: theme.bg,
                      color: theme.text,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                    }}
                  >
                    {SIZES.map(s => (
                      <option key={s} value={s}>
                        {s} faxes
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {agent && (
                <div
                  style={{
                    marginTop: '0.9rem',
                    fontSize: '0.76rem',
                    color: theme.textMuted,
                    background: theme.bg,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 4,
                    padding: '0.6rem',
                  }}
                >
                  {/* THE ACCOUNT AND REGION, AGAINST EVERY TARGET. A misdeployed agent
                      offering production pods is visible here rather than silent. */}
                  <div>
                    Agent reports account{' '}
                    <strong style={{ color: theme.text }}>
                      {agent.reported_aws_account || 'not reported'}
                    </strong>{' '}
                    in region{' '}
                    <strong style={{ color: theme.text }}>
                      {agent.reported_aws_region || 'not reported'}
                    </strong>
                    {agent.reported_image_version ? ` · image ${agent.reported_image_version}` : ''}
                  </div>
                  {target && (
                    <div style={{ marginTop: '0.3rem' }}>
                      Environment{' '}
                      <strong style={{ color: theme.text }}>
                        {environmentLabel(target, notRecorded)}
                      </strong>{' '}
                      · {target.dns_name} ·{' '}
                      {/* NOT a "pod available" badge. Capacity OBSERVED, and nothing we
                          can query says the pod is out of customer routing. */}
                      {target.healthy_count === null || target.healthy_count === undefined
                        ? 'healthy targets not observed'
                        : `${target.healthy_count} healthy — capacity observed at launch`}
                    </div>
                  )}
                </div>
              )}

              {/* THE SERVER'S OWN WORDS, for both limits. Rendered, never re-worded. */}
              {job && (
                <div
                  style={{
                    marginTop: '0.7rem',
                    fontSize: '0.76rem',
                    color: '#ffa726',
                    maxWidth: '46rem',
                  }}
                >
                  <div>{job.isolation.note}</div>
                  <div style={{ marginTop: '0.35rem' }}>{job.isolation.drain_state_note}</div>
                </div>
              )}

              {confirming && target && agent && job && (
                <div
                  style={{
                    marginTop: '0.9rem',
                    background: theme.bg,
                    color: theme.text,
                    border: '1px solid #6a5a2a',
                    borderRadius: 4,
                    padding: '0.7rem',
                    fontSize: '0.8rem',
                    maxWidth: '46rem',
                  }}
                >
                  {/* THE ENVIRONMENT IS RESTATED HERE because this is the last moment it
                      can be caught: dev-ccs1 and qa-ccs1 share one account and one
                      region, so nothing downstream can tell them apart for the
                      operator. */}
                  <strong>
                    Send {size} real fax{size === 1 ? '' : 'es'} to the{' '}
                    {environmentLabel(target, notRecorded)} pod{' '}
                    {target.pod || target.load_balancer_name}?
                  </strong>
                  <div style={{ color: theme.textMuted, marginTop: '0.4rem' }}>
                    Relayed by {agent.name} in account{' '}
                    {agent.reported_aws_account || 'not reported'} ·{' '}
                    {agent.reported_aws_region || 'not reported'}, on job {job.name}. Every item
                    is a real fax with real per-page cost.
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.7rem' }}>
                    <button
                      onClick={launch}
                      disabled={launching}
                      style={{
                        padding: '0.45rem 1rem',
                        background: theme.primary,
                        color: theme.onPrimary,
                        border: 'none',
                        borderRadius: 4,
                        cursor: launching ? 'default' : 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      {launching
                        ? 'Launching…'
                        : `Confirm — send to ${environmentLabel(target, notRecorded)}`}
                    </button>
                    <button
                      onClick={() => setConfirming(false)}
                      style={{
                        padding: '0.45rem 0.9rem',
                        background: 'transparent',
                        color: theme.textMuted,
                        border: `1px solid ${theme.border}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                      }}
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}

              {!confirming && (
                <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.9rem' }}>
                  <button
                    onClick={() => setConfirming(true)}
                    disabled={!ready}
                    style={{
                      padding: '0.5rem 1.1rem',
                      background: ready ? theme.primary : theme.bgElevated,
                      color: ready ? theme.onPrimary : theme.textMuted,
                      border: 'none',
                      borderRadius: 4,
                      cursor: ready ? 'pointer' : 'default',
                      fontWeight: 600,
                    }}
                  >
                    Review and send {size} faxes
                  </button>
                  <button
                    onClick={() => {
                      setShowLaunch(false)
                      setConfirming(false)
                    }}
                    style={{
                      padding: '0.5rem 0.9rem',
                      background: 'transparent',
                      color: theme.textMuted,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {batches.length === 0 ? (
        <Empty>
          <strong>No internal batches yet.</strong>
          <div style={{ color: theme.textMuted, marginTop: '0.35rem' }}>
            {isAdmin
              ? 'Launch one to measure a single pod in isolation on its outbound leg.'
              : 'An administrator launches these.'}
          </div>
        </Empty>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ background: theme.bgElevated, color: theme.text, textAlign: 'left' }}>
              <th style={{ padding: '0.5rem' }}>Started</th>
              <th style={{ padding: '0.5rem' }}>Environment</th>
              <th style={{ padding: '0.5rem' }}>Pod</th>
              <th style={{ padding: '0.5rem' }}>Agent</th>
              <th style={{ padding: '0.5rem' }}>Job</th>
              <th style={{ padding: '0.5rem' }}>Size</th>
              <th style={{ padding: '0.5rem' }}>Status</th>
              <th style={{ padding: '0.5rem' }}>Progress</th>
              <th style={{ padding: '0.5rem' }}>Open</th>
              <th style={{ padding: '0.5rem' }}>Pod check</th>
              <th style={{ padding: '0.5rem' }} />
            </tr>
          </thead>
          <tbody>
            {batches.map(b => (
              <tr key={b.id} style={{ borderTop: `1px solid ${theme.border}` }}>
                <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                  {fmtDateTime(b.t_created)}
                </td>
                {/* THE SAME RULE AS THE PICKER, on a batch that already ran: the server
                    publishes `target_environment_recorded` beside the nullable column, so
                    a missing tag prints the declared words rather than a guess. */}
                <td style={{ padding: '0.5rem' }}>
                  {b.target_environment_recorded && b.target_environment
                    ? b.target_environment
                    : notRecorded}
                </td>
                <td style={{ padding: '0.5rem' }}>{b.target_pod || '—'}</td>
                <td style={{ padding: '0.5rem' }}>{b.agent_name || '—'}</td>
                <td style={{ padding: '0.5rem' }}>{b.job_name || b.job_id.slice(0, 8)}</td>
                <td style={{ padding: '0.5rem' }}>{b.size}</td>
                <td style={{ padding: '0.5rem' }}><StatusBadge status={b.status} /></td>
                <td style={{ padding: '0.5rem' }}><Progress b={b} /></td>
                <td style={{ padding: '0.5rem' }}><OpenItems b={b} /></td>
                <td style={{ padding: '0.5rem' }}>
                  <PodCheck outcome={b.pod_cross_check_outcome} />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <Link to={`/tactical/${b.id}`} style={{ color: theme.primary, fontSize: '0.8rem' }}>
                    Report →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
